import React, { Component } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Global error boundary component.
 * Catches unhandled React errors in the component tree and displays a friendly fallback UI.
 * This is the one exception to the "functional components only" rule, as error boundaries
 * require the componentDidCatch lifecycle method.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            px: 3,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
          }}
          role="alert"
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#212121' }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#616161', maxWidth: 480 }}>
            An unexpected error occurred. Please try reloading the page. If the problem persists, contact support.
          </Typography>
          <Button
            variant="contained"
            onClick={this.handleReload}
            sx={{ borderRadius: '20px', px: 4, py: 1 }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
